import { Component, computed, input } from '@angular/core';

import { IconLibrary } from '@finnairoyj/fcom-ui-styles';

import { CmsArticle, CmsPicture, CmsTaxonomyGeneral, CmsVideo } from '@fcom/core-api';
import { isNotBlank } from '@fcom/core/utils/utils';
import { type IconName } from '@fcom/ui-components/components/icons/icons.interfaces';

export const allowedBackgrounds: Record<string, string> = {
  'nordic-blue-25-bg': 'nordic-blue-25-bg signal-information-notification-bg',
  'nordic-blue-50-bg': 'nordic-blue-50-bg signal-information-notification-bg',
  'nordic-blue-50-bg signal-information-notification-bg': 'nordic-blue-50-bg signal-information-notification-bg',
  'signal-information-notification-bg': 'signal-information-notification-bg',
};

@Component({
  selector: 'cms-rich-text',
  template: `
    <fin-rich-text
      [class]="hostClasses()"
      [content]="content()"
      [headerLevel]="headerLevel()"
      [headerStyles]="headerStyles()"
      [hideMediaContentImage]="hideMediaContentImage()"
      [icons]="icons()"
      [media]="media()"
      [scaleImage]="scaleImage()"
      [styleClasses]="styleClasses()"
      [title]="title()"
    />
  `,
  standalone: false,
})
export class CmsRichTextComponent {
  content = input.required<CmsArticle>();
  headerLevel = input<number>();
  headerStyles = input<string[]>([]);
  scaleImage = input(true);
  extraClasses = input('mr-large-b');
  hideMediaContentImage = input(false);

  title = computed(() => {
    const content = this.content();
    const hasNoTitleTag = (content?.subjectTaxonomyTags ?? []).includes(CmsTaxonomyGeneral.NO_TITLE);
    return !hasNoTitleTag && isNotBlank(content?.teaserTitle) ? content.teaserTitle : undefined;
  });

  media = computed(() => {
    return (this.content()?.media ?? []) as CmsPicture[] | CmsVideo[];
  });

  icons = computed<Array<{ name: IconName; category?: IconLibrary }> | undefined>(() => {
    const localSettings = this.content()?.localSettings;
    if (!localSettings?.iconName) {
      return undefined;
    }
    const names = localSettings.iconName;
    const nameArr = Array.isArray(names) ? names : [names];
    const categories = localSettings?.iconCategory;
    const categoryArr = Array.isArray(categories) ? categories : [categories];
    return nameArr.map((name, index) => {
      return {
        name,
        category: categoryArr?.[index] ?? IconLibrary.SVG_LIBRARY,
      };
    });
  });

  styleClasses = computed(() => {
    return (this.content()?.subjectTaxonomyTags ?? []).filter((tag) => tag.startsWith('cmstyle-'));
  });

  protected hostClasses = computed(() => {
    const bg = this.content()?.subjectTaxonomyTags?.find((tag) => tag in allowedBackgrounds);

    if (bg) {
      const bgClasses = allowedBackgrounds[bg];

      return `block ${this.extraClasses()} ${bgClasses} pr-small radius-xxsmall`;
    }

    return `block ${this.extraClasses()}`;
  });
}
